/** Entrada de precio de un plan para UNA divisa concreta (espejo del `PlanPrice` del backend, D2/D10). */
export interface PlanPricePrimitive {
  currency: string;
  amount: number;
  stripePriceId: string;
  /**
   * Intervalo de facturación de esta entrada ('month'/'year', monthly-billing-interval D2).
   * Opcional por retrocompatibilidad de hidratación: una entrada sin este campo (catálogo
   * previo a este change) SHALL tratarse como 'year' — mismo criterio tolerante que aplica el
   * backend en `PlanPrice.fromPersistence`. Nunca se lee directamente; usar `Plan.priceIn()`.
   */
  interval?: string;
}

export class Plan {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly users: number,
    public readonly tasks: number,
    public readonly connections: number,
    public readonly workspaces: number,
    // Campos comerciales (multi-currency-plan-catalog-checkout, D10): sustituye los antiguos
    // `price: number`/`currency: string` planos (un plan = un precio = una divisa) por un array
    // con una entrada por divisa soportada — espejo de `Plan.prices: PlanPrice[]` del backend.
    // `stripePriceId` viaja DENTRO de cada entrada (antes era un campo suelto del plan).
    public readonly prices: PlanPricePrimitive[] = [],
    public readonly featured: boolean = false,
    public readonly order: number = 0,
    // Disponibilidad por organización (slice 5, modo logado): el endpoint autenticado
    // GET /v2/organizations/plans los rellena tras contar el uso vivo de la org.
    // Defaults `true`/`[]` → el modo anónimo (GET /v2/public/plans), que no los recibe,
    // los trata como disponibles sin bloqueo.
    public readonly available: boolean = true,
    public readonly blockedBy: string[] = [],
    // Plan destacado para ESTA organización (usage-based-plan-recommendation): lo calcula el
    // endpoint autenticado contra el uso real. Opcional a propósito — el catálogo anónimo
    // (GET /v2/public/plans) no lo emite, porque sin sesión no hay uso que medir, y ahí
    // `featured` sigue siendo la única señal. Leer siempre vía `isRecommended()`.
    public readonly recommended?: boolean
  ) {}

  /**
   * El plan que debe aparecer destacado (badge "RECOMENDADO" + card resaltada).
   *
   * `recommended` cuando el backend lo emite, `featured` como fallback. El fallback vive aquí
   * y no en cada punto de consumo —hoy la variante visual de la card y el badge, mañana quizá
   * otro— porque duplicar el `??` es la vía por la que una de las copias se queda atrás en el
   * siguiente refactor. Un `false` explícito del backend gana: significa "no destacar nada".
   */
  isRecommended(): boolean {
    return this.recommended ?? this.featured;
  }

  static fromPrimitives(data: {
    id: string;
    name: string;
    users: number;
    tasks: number;
    connections: number;
    workspaces: number;
    prices?: PlanPricePrimitive[];
    featured?: boolean;
    order?: number;
    available?: boolean;
    blockedBy?: string[];
    recommended?: boolean;
  }): Plan {
    return new Plan(
      data.id,
      data.name,
      data.users,
      data.tasks,
      data.connections,
      data.workspaces,
      data.prices ?? [],
      data.featured ?? false,
      data.order ?? 0,
      data.available ?? true,
      data.blockedBy ?? [],
      // Sin `?? false`: la ausencia del campo (catálogo anónimo) tiene que llegar como
      // `undefined` para que `isRecommended()` pueda caer a `featured`.
      data.recommended
    );
  }

  toPrimitives() {
    return {
      id: this.id,
      name: this.name,
      users: this.users,
      tasks: this.tasks,
      connections: this.connections,
      workspaces: this.workspaces,
      prices: this.prices,
      featured: this.featured,
      order: this.order,
      available: this.available,
      blockedBy: this.blockedBy,
      recommended: this.recommended,
    };
  }

  /**
   * Busca la entrada de precio para `currency` (código en mayúsculas, p.ej. 'EUR'/'USD') Y
   * `interval` ('month'/'year', monthly-billing-interval D2) — ambos argumentos obligatorios,
   * sin default en la firma (mismo criterio que `Plan.priceIn` del backend): quien no sepa el
   * intervalo lo resuelve explícitamente en su capa. Una entrada sin `interval` se trata como
   * 'year' (retrocompatibilidad de hidratación, ver `PlanPricePrimitive`).
   */
  priceIn(currency: string, interval: string): PlanPricePrimitive | undefined {
    return this.prices.find(p => p.currency === currency && (p.interval ?? 'year') === interval);
  }

  /** true si el plan tiene al menos una entrada de precio (en cualquier divisa) — sustituye al
   * antiguo sentinel `price === 0` para distinguir un plan de pago de uno "A Medida" (Enterprise). */
  isPayable(): boolean {
    return this.prices.length > 0;
  }
}
